import { FormControl, InputLabel, MenuItem, Select, Box, SelectChangeEvent } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Fragment } from 'react/jsx-runtime';
import { memo } from 'react';
import ChartHelpTooltip from '../common/ChartHelpTooltip';
import { useFormContext } from '../../contexts/FormContext';
import { useFieldValue } from '../../hooks/useFormValues';
import type { FieldComponentProps } from '../form-engine/types';

const SelectField = ({ field }: FieldComponentProps) => {
  const { handleChange } = useFormContext();

  const selectedValue = useFieldValue(field.name!) ?? field.default ?? '';

  // Helper function to serialize values for comparison
  const serializeValue = <T,>(value: T) => {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value);
    }
    return value;
  };

  // Helper function to check if two values are equal (handles objects)
  const areValuesEqual = (val1: unknown, val2: unknown) => {
    if (typeof val1 === 'object' && typeof val2 === 'object' && val1 !== null && val2 !== null) {
      return JSON.stringify(val1) === JSON.stringify(val2);
    }
    return val1 === val2;
  };

  // Find the matching option value for the current selectedValue
  const normalizedValue = (() => {
    if (!selectedValue) return '';

    // Try to find a matching option
    const matchingOption = field.options!.find((option) => areValuesEqual(option.value, selectedValue));

    if (matchingOption) {
      // For object values, use the serialized form
      return typeof matchingOption.value === 'object' && matchingOption.value !== null
        ? serializeValue(matchingOption.value)
        : matchingOption.value;
    }

    // If no match found, try to serialize if it's an object
    return typeof selectedValue === 'object' && selectedValue !== null ? serializeValue(selectedValue) : selectedValue;
  })();

  // Handle change event
  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value;

    // Try to deserialize if it's a string that looks like JSON
    let actualValue: unknown = value;
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try {
        actualValue = JSON.parse(value);
      } catch {
        // If parsing fails, use the original value
        actualValue = value;
      }
    }

    // Create a new event with the deserialized value
    const newEvent = {
      ...e,
      target: {
        ...e.target,
        value: actualValue as string,
      },
    };

    handleChange(newEvent, field);
  };

  return (
    <Grid size={field.size || 12} key={field.name}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl fullWidth required={field.required || false} disabled={!!field.disabled}>
          <InputLabel>
            <Fragment>
              {field.label}
              {Object.prototype.hasOwnProperty.call(field, 'help') ? <ChartHelpTooltip chartType={field.help!} type="label" /> : null}
            </Fragment>
          </InputLabel>
          <Select label={field.label} name={field.name} value={normalizedValue} onChange={handleSelectChange}>
            {field.options!.map((option, index) => {
              const optionValue = typeof option.value === 'object' && option.value !== null ? serializeValue(option.value) : option.value;

              return (
                <MenuItem key={index} value={optionValue}>
                  {option.label}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        {Object.prototype.hasOwnProperty.call(field, 'dynamicHelp') ? (
          <ChartHelpTooltip chartType={field.dynamicHelp as string} type="chart" selectedValue={selectedValue} />
        ) : null}
      </Box>
    </Grid>
  );
};

export default memo(SelectField);
